import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HeroBanner from '@components/layout/HeroBanner';
import CategoryGrid from '@components/layout/CategoryGrid';
import CredibilityIcons from '@components/layout/CredibilityIcons';
import RelatedProductsCarousel from '@components/products/RelatedProductsCarousel';
import QuoteModal from '@components/common/QuoteModal';
import { FiShield, FiClock, FiAward, FiPhone } from 'react-icons/fi';
import { useProducts } from '@hooks/useProducts';
import { useCart } from '@hooks/useCart';
import Loading from '@components/common/Loading';

/**
 * HomePage
 * Includes: Header, HeroBanner, CategoryGrid (products), CategoryGrid (services), 
 * CredibilityIcons, FeaturedProductsCarousel, Testimonials, B2B banner, Footer
 * Features:
 * - Fetch featured products/services via hooks
 * - Animations: staggered fade-in
 */

const HomePage = () => {
  const navigate = useNavigate();
  const { products, loading } = useProducts({ featured: true, limit: 8 });
  const { addToCart } = useCart();
  const [showQuoteModal, setShowQuoteModal] = useState(false);

  const productCategories = [
    { image: '/images/1.jpg', title: 'Security Cameras', link: '/products?category=cameras' },
    { image: '/images/2.jpg', title: 'Alarm Systems', link: '/products?category=alarms' },
    { image: '/images/3.jpg', title: 'Access Control', link: '/products?category=access-control' },
    { image: '/images/4.jpg', title: 'Smart Locks', link: '/products?category=smart-locks' },
  ];

  const serviceCategories = [
    { image: '/images/5.jpg', title: 'Installation Services', link: '/services?type=installation' },
    { image: '/images/6.jpg', title: 'Maintenance', link: '/services?type=maintenance' },
    { image: '/images/7.jpg', title: 'Consultation', link: '/services?type=consultation' },
  ];

  const credibilityItems = [
    {
      icon: <FiShield />,
      title: 'Secure & Certified',
      description: 'All products meet industry standards',
    },
    {
      icon: <FiClock />,
      title: '24/7 Support',
      description: 'Round-the-clock customer service',
    },
    {
      icon: <FiAward />,
      title: 'Award Winning',
      description: 'Recognized for excellence',
    },
    {
      icon: <FiPhone />,
      title: 'Expert Advice',
      description: 'Professional consultation available',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Banner */}
      <HeroBanner
        imageUrl="/images/9.jpg"
        title="PROFESSIONAL SECURITY SOLUTIONS"
        subtitle="Trusted by businesses and homeowners across the UK for over 20 years"
        primaryCTA={{
          text: 'Get a Quote',
          onClick: () => setShowQuoteModal(true),
        }}
        secondaryCTA={{
          text: 'View Products',
          onClick: () => navigate('/products'),
        }}
      />

      {/* Statistics Section - Gough Kelly Style */}
      <section className="bg-primary-800 py-16">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <div className="text-5xl font-bold mb-2">2,500+</div>
              <div className="text-primary-100 uppercase text-sm tracking-wide">Projects Completed</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">20+</div>
              <div className="text-primary-100 uppercase text-sm tracking-wide">Years Experience</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">1,200+</div>
              <div className="text-primary-100 uppercase text-sm tracking-wide">Happy Clients</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">24/7</div>
              <div className="text-primary-100 uppercase text-sm tracking-wide">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Credibility Icons */}
      <CredibilityIcons items={credibilityItems} />

      {/* Product Categories */}
      <CategoryGrid title="Shop by Category" items={productCategories} />

      {/* Featured Products */}
      {loading ? (
        <div className="py-12">
          <Loading text="Loading featured products..." />
        </div>
      ) : (
        <RelatedProductsCarousel
          products={products}
          onAddToCart={addToCart}
        />
      )}

      {/* Services */}
      <CategoryGrid title="OUR SERVICES" items={serviceCategories} />

      {/* Client Logos Section - Gough Kelly Style */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <h2 className="text-center text-sm uppercase tracking-widest text-gray-600 mb-8">
            Trusted by Leading Organizations
          </h2>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-8 items-center opacity-60 grayscale">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="flex items-center justify-center">
                <div className="w-24 h-24 bg-gray-300 rounded flex items-center justify-center">
                  <span className="text-gray-500 text-xs">Client {i}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2B Banner - Gough Kelly Style */}
      <section className="relative bg-primary-800 text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900 to-primary-700 opacity-90"></div>
        <div className="container-custom text-center relative z-10">
          <p className="text-accent-500 uppercase text-sm tracking-widest mb-4 font-semibold">
            For Business Customers
          </p>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            COMMERCIAL SECURITY SOLUTIONS
          </h2>
          <p className="text-xl mb-8 text-primary-100 max-w-2xl mx-auto">
            Comprehensive security packages tailored for businesses with dedicated account management and priority support
          </p>
          <button
            onClick={() => setShowQuoteModal(true)}
            className="btn-primary inline-block"
          >
            Request a Quote
          </button>
        </div>
      </section>

      {/* Testimonials - Gough Kelly Style */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-accent-500 uppercase text-sm tracking-widest mb-2 font-semibold">
              Client Testimonials
            </p>
            <h2 className="text-4xl font-bold text-primary-800">WHAT OUR CLIENTS SAY</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                text: "Professional service from start to finish. The team installed our complete security system efficiently and provided excellent training.",
                name: "Sarah Johnson",
                company: "Johnson Properties Ltd",
              },
              {
                text: "Outstanding quality products and responsive support. They've been maintaining our systems for 5 years now without any issues.",
                name: "Michael Chen",
                company: "TechCorp Industries",
              },
              {
                text: "Highly recommend SecureGuard for commercial installations. Their expertise and attention to detail is second to none.",
                name: "David Williams",
                company: "Williams Retail Group",
              },
            ].map((testimonial, i) => (
              <div key={i} className="bg-gray-50 p-8 rounded-lg border-l-4 border-accent-500">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, j) => (
                    <span key={j} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">
                  "{testimonial.text}"
                </p>
                <p className="font-bold text-primary-800">{testimonial.name}</p>
                <p className="text-sm text-gray-600">{testimonial.company}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Modal */}
      <QuoteModal
        isOpen={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
      />
    </div>
  );
};

export default HomePage;

